import styles from './styles.module.scss';

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: string;
  /** Optional tabular count rendered after the label. */
  count?: number;
  testId?: string;
}

export interface SegmentedControlProps<T extends string> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Accessible name of the group. */
  ariaLabel: string;
  /**
   * Fill the container width, sharing it between segments; segments keep
   * their content width and the bar scrolls when they do not fit.
   */
  stretch?: boolean;
  className?: string;
}

/**
 * Single-select framed bar of segments; the selected one is filled with
 * `--highlight`. Scrolls sideways when it does not fit the available width.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  stretch = false,
  className,
}: SegmentedControlProps<T>) {
  return (
    <div
      className={[styles.bar, stretch && styles.stretch, className].filter(Boolean).join(' ')}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            className={styles.segment}
            onClick={() => onChange(option.value)}
            data-testid={option.testId}
          >
            <span>{option.label}</span>
            {option.count !== undefined && <span className={styles.count}>{option.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
